import { Button } from "@/components/ui/button";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Zap,
  Trash2,
  RefreshCw,
  ScanSearch,
  Loader2
} from "lucide-react";
import { useState } from "react";
import { SignalCategory } from "@/hooks/useEnterpriseSignals";
import { useLocalization } from '@/hooks/useLocalization';

interface SignalControlsProps {
  onGenerate: (params: { count: number; category?: SignalCategory }) => void;
  onScan: () => void;
  onClear: () => void;
  onRefresh: () => void;
  isGenerating: boolean;
  isScanning: boolean;
  isClearing: boolean;
}

export function SignalControls({
  onGenerate,
  onScan,
  onClear,
  onRefresh,
  isGenerating,
  isScanning,
  isClearing,
}: SignalControlsProps) {
  const { t } = useLocalization();
  const [count, setCount] = useState(5);
  const [category, setCategory] = useState<SignalCategory | 'all'>('all');

  const handleGenerate = () => {
    onGenerate({
      count,
      category: category === 'all' ? undefined : category,
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-muted/50 rounded-lg border">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{t('Generate')}</span>
        <Input
          type="number"
          min={1}
          max={50}
          value={count}
          onChange={(e) => setCount(parseInt(e.target.value) || 5)}
          className="w-20 h-9"
        />
        <span className="text-sm text-muted-foreground">{t('signals')}</span>
      </div>

      <Select value={category} onValueChange={(v) => setCategory(v as SignalCategory | 'all')}>
        <SelectTrigger className="w-40 h-9">
          <SelectValue placeholder={t('Category')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('All Categories')}</SelectItem>
          <SelectItem value="project">{t('Project Events')}</SelectItem>
          <SelectItem value="communication">{t('Communication')}</SelectItem>
          <SelectItem value="governance">{t('Governance')}</SelectItem>
        </SelectContent>
      </Select>

      <Button onClick={handleGenerate} disabled={isGenerating} size="sm">
        {isGenerating ? (
          <Loader2 className="h-4 w-4 me-2 animate-spin" />
        ) : (
          <Zap className="h-4 w-4 me-2" />
        )}
        
        {t('Generate')}
      </Button>

      <Button onClick={onScan} disabled={isScanning} size="sm" variant="secondary">
        {isScanning ? (
          <Loader2 className="h-4 w-4 me-2 animate-spin" />
        ) : (
          <ScanSearch className="h-4 w-4 me-2" />
        )}
        
        {t('Scan Synced Data')}
      </Button>

      <div className="flex-1" />

      <Button variant="outline" size="sm" onClick={onRefresh}>
        <RefreshCw className="h-4 w-4 me-2" />
        
        {t('Refresh')}
      </Button>

      <Button 
        variant="destructive" 
        size="sm" 
        onClick={onClear}
        disabled={isClearing}
      >
        {isClearing ? (
          <Loader2 className="h-4 w-4 me-2 animate-spin" />
        ) : (
          <Trash2 className="h-4 w-4 me-2" />
        )}
        
        {t('Clear All')}
      </Button>
    </div>
  );
}
